/**
 * mLearning — Ô soạn công thức toán (MathLive), dạng panel nổi.
 *
 * ============================================================
 * SỬA LỖI "KHÔNG GÕ ĐƯỢC"
 * ============================================================
 * Bản trước dùng thẻ <math-field> trong JSX rồi mới `await import('mathlive')`.
 * Custom element được nâng cấp (upgrade) ngay khi thư viện nạp xong, tức là
 * TRƯỚC khi mình kịp gán MathfieldElement.fontsDirectory và gắn listener —
 * ô hiện ra nhưng không nhận phím.
 *
 * Bản này đổi sang cách chắc chắn hơn:
 *   1. import tĩnh MathfieldElement ở đầu file
 *   2. cấu hình fontsDirectory / soundsDirectory ngay ở phạm vi module,
 *      trước khi bất kỳ element nào được tạo
 *   3. tạo element bằng `new MathfieldElement()` rồi mới gắn vào DOM
 * Nhờ vậy thứ tự luôn đúng: cấu hình -> tạo -> gắn listener -> hiển thị.
 *
 * ============================================================
 * VÌ SAO KHÔNG DÙNG position: fixed
 * ============================================================
 * App React nằm trong iframe của Angular, và App.tsx đang gửi
 * `contentHeight` để Angular NỚI IFRAME CAO BẰNG NỘI DUNG. Nghĩa là iframe
 * không hề cuộn — trang Angular mới là chỗ cuộn.
 *
 * Với cấu trúc đó, `position: fixed` bên trong React chỉ neo vào khung nhìn
 * của iframe (cao bằng cả nội dung), nên panel sẽ nằm lì ở đáy trang chứ
 * không đi theo mắt người dùng. Đúng hiện tượng bạn gặp.
 *
 * Cách xử lý: Angular gửi sang vùng đang nhìn thấy, React đặt panel bằng
 * `position: absolute` theo toạ độ đó. Xem HUONG-DAN.md để lấy đoạn code
 * cần thêm bên Angular. Nếu không nhận được tin nào (chạy React độc lập,
 * không nhúng iframe) thì tự động rơi về `position: fixed`.
 */

import React from 'react';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSquareRootVariable,
    faCopy,
    faTrashAlt,
    faXmark
} from '@fortawesome/free-solid-svg-icons';
import { MathfieldElement } from 'mathlive';

import {
    bocLatex,
    boDauBoc,
    chenVaoOTextDangFocus
} from '../services/H5pMathInsert';

import './CongThucToan.css';

const CDN_MATHLIVE = 'https://cdn.jsdelivr.net/npm/mathlive@0.101.0/dist';

// Cấu hình ở phạm vi module: chạy đúng một lần, trước mọi element.
MathfieldElement.fontsDirectory = `${CDN_MATHLIVE}/fonts`;
MathfieldElement.soundsDirectory = `${CDN_MATHLIVE}/sounds`;

const CHIEU_CAO_PANEL = 460;
const LE_DAY = 16;

interface MauCongThuc {
    nhan: string;
    latex: string;
}

const MAU_CONG_THUC: MauCongThuc[] = [
    { nhan: 'Phân số', latex: '\\frac{a}{b}' },
    { nhan: 'Căn bậc hai', latex: '\\sqrt{x}' },
    { nhan: 'Căn bậc n', latex: '\\sqrt[3]{x}' },
    { nhan: 'Lũy thừa', latex: 'x^{2}' },
    { nhan: 'Chỉ số dưới', latex: 'x_{1}' },
    { nhan: 'Tích phân', latex: '\\int_{0}^{1} x\\,dx' },
    { nhan: 'Tổng sigma', latex: '\\sum_{i=1}^{n} i' },
    { nhan: 'Giới hạn', latex: '\\lim_{x \\to 0} f(x)' },
    { nhan: 'Hệ phương trình', latex: '\\begin{cases} x+y=5 \\\\ x-y=1 \\end{cases}' },
    { nhan: 'Ma trận', latex: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}' },
    { nhan: 'Vectơ', latex: '\\overrightarrow{AB}' },
    { nhan: 'Phản ứng hoá học', latex: '\\ce{2H2 + O2 -> 2H2O}' }
];

interface Props {
    giaTriBanDau?: string;
    mocDinhMo?: boolean;
    onDaChen?: (latexDaBoc: string) => void;
}

interface State {
    moRong: boolean;
    kieuBlock: boolean;
    latexThuan: string;
    thongBao: string;
    loaiThongBao: 'ok' | 'loi' | '';
    /** Đáy vùng nhìn thấy, tính theo toạ độ bên trong iframe React. */
    dayVungNhin: number | null;
}

export default class CongThucToan extends React.Component<Props, State> {
    private hopChuaRef = React.createRef<HTMLDivElement>();
    private mathField?: MathfieldElement;
    private timerThongBao?: ReturnType<typeof setTimeout>;

    constructor(props: Props) {
        super(props);
        const boc = boDauBoc(props.giaTriBanDau ?? '');
        this.state = {
            moRong: props.mocDinhMo ?? false,
            kieuBlock: boc.block,
            latexThuan: boc.latex,
            thongBao: '',
            loaiThongBao: '',
            dayVungNhin: null
        };
    }

    public componentDidMount(): void {
        window.addEventListener('message', this.nhanTinTuAngular, false);
        this.dungOMathField();
    }

    public componentDidUpdate(_p: Props, prevState: State): void {
        if (!prevState.moRong && this.state.moRong) {
            // Phòng hờ: nếu tham chiếu cũ còn sót mà element đã rời khỏi DOM
            // thì bỏ đi, để dungOMathField() tạo ô mới.
            if (this.mathField && !this.mathField.isConnected) {
                this.mathField = undefined;
            }
            this.dungOMathField();
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener('message', this.nhanTinTuAngular, false);
        if (this.timerThongBao) {
            clearTimeout(this.timerThongBao);
        }
    }

    /**
     * Thu gọn panel.
     *
     * PHẢI xoá tham chiếu this.mathField ở đây. Khi thu gọn, React tháo hộp
     * chứa <div class="ctt__hop"> khỏi DOM, kéo theo cả MathfieldElement bên
     * trong. Nhưng this.mathField vẫn trỏ vào element cũ đã mồ côi, nên lần
     * mở lại dungOMathField() thoát sớm ở nhánh `if (this.mathField) return`
     * và không tạo ô mới -> panel mở ra mà không thấy ô gõ đâu.
     *
     * Giá trị công thức không mất vì đã nằm ở state.latexThuan.
     */
    private thuGon = () => {
        this.mathField = undefined;
        this.setState({ moRong: false });
    };

    /** Nạp công thức có sẵn vào ô soạn (dùng khi sửa công thức cũ). */
    public napCongThuc(chuoiCoTheCoDauBoc: string): void {
        const boc = boDauBoc(chuoiCoTheCoDauBoc);
        this.setState(
            {
                latexThuan: boc.latex,
                kieuBlock: boc.block,
                moRong: true,
                thongBao: '',
                loaiThongBao: ''
            },
            () => {
                if (this.mathField) {
                    this.mathField.value = boc.latex;
                }
            }
        );
    }

    /**
     * Angular gửi sang vùng đang nhìn thấy để panel bám theo màn hình.
     *
     * Dùng type riêng 'mlViewport'. KHÔNG dùng lại 'whenParentScroll' vì
     * App.tsx đang xử lý type đó bằng cách đọc event.data.scrollBottom rồi
     * gọi loadWIRIS(); tin này bắn theo nhịp khung hình và không có
     * scrollBottom nên sẽ làm hỏng luồng đó.
     */
    private nhanTinTuAngular = (event: MessageEvent) => {
        const d: any = event.data;
        if (d?.type !== 'mlViewport') {
            return;
        }
        if (typeof d.dayVungNhin === 'number' && d.dayVungNhin > 0) {
            this.setState({ dayVungNhin: d.dayVungNhin });
        }
    };

    /**
     * Tạo <math-field> bằng JS thay vì JSX.
     * Cấu hình đã set ở phạm vi module nên element sinh ra là dùng được ngay.
     */
    private dungOMathField(): void {
        const hop = this.hopChuaRef.current;
        if (!hop || this.mathField) {
            return;
        }

        const mf = new MathfieldElement();
        mf.className = 'ctt__field';
        mf.setAttribute('aria-label', 'Ô nhập công thức toán');
        // 'manual' để bàn phím ảo chỉ hiện khi bấm nút, không tự bung ra
        // che mất nội dung bài giảng.
        mf.mathVirtualKeyboardPolicy = 'manual';
        mf.value = this.state.latexThuan;

        mf.addEventListener('input', () => {
            this.setState({
                latexThuan: mf.value ?? '',
                thongBao: '',
                loaiThongBao: ''
            });
        });

        hop.appendChild(mf);
        this.mathField = mf;

        // ---- Sửa lỗi mất focus sau khi bấm vào ô của H5P ----
        //
        // Ô soạn thảo H5P nằm trong iframe. Khi giáo viên bấm vào đó, focus
        // bàn phím chuyển sang tài liệu của iframe. Bấm ngược lại vào ô
        // MathLive thì element được chọn về mặt thị giác (viền xanh sáng lên)
        // nhưng ô nhận phím bên trong của MathLive (keyboard sink) KHÔNG được
        // lấy lại focus -> gõ không ăn.
        //
        // Đó là lý do bấm nút "Phân số" thì gõ được: các nút đó gọi mf.focus().
        //
        // Cách chữa: tự gọi focus() khi có thao tác chuột lên ô. Dùng
        // pointerdown ở pha capture để chạy TRƯỚC bộ xử lý của MathLive, nhờ
        // vậy MathLive vẫn đặt được con trỏ đúng chỗ vừa bấm.
        hop.addEventListener(
            'pointerdown',
            () => {
                if (!mf.hasFocus()) {
                    mf.focus();
                }
            },
            true
        );

        // Cho con trỏ vào ô luôn để giáo viên gõ được ngay
        setTimeout(() => mf.focus(), 0);
    }

    private get ketQua(): string {
        return bocLatex(this.state.latexThuan, this.state.kieuBlock);
    }

    private get coNoiDung(): boolean {
        return this.ketQua.length > 0;
    }

    private doiKieu = (block: boolean) => {
        this.setState({ kieuBlock: block, thongBao: '', loaiThongBao: '' });
        this.layLaiFocus();
    };

    private chenMau = (latex: string) => {
        const mf = this.mathField;
        if (!mf) {
            return;
        }
        mf.insert(latex, { format: 'auto' });
        mf.focus();
        this.setState({ latexThuan: mf.value ?? '', thongBao: '', loaiThongBao: '' });
    };

    private xoaHet = () => {
        if (this.mathField) {
            this.mathField.value = '';
            this.mathField.focus();
        }
        this.setState({ latexThuan: '', thongBao: '', loaiThongBao: '' });
    };

    /**
     * Lấy lại focus cho ô nhập.
     *
     * Gọi sau các thao tác đổi kiểu inline/block — những nút đó nằm ngoài ô
     * nhập nên bấm vào là ô mất focus, giáo viên phải bấm lại mới gõ tiếp
     * được.
     */
    private layLaiFocus(): void {
        const mf = this.mathField;
        if (mf && !mf.hasFocus()) {
            mf.focus();
        }
    }

    private chenVaoBaiGiang = () => {
        if (!this.coNoiDung) {
            return;
        }
        const kq = chenVaoOTextDangFocus(this.ketQua);
        switch (kq) {
            case 'da-chen':
                this.hienThongBao('Đã chèn vào bài giảng', 'ok');
                this.props.onDaChen?.(this.ketQua);
                break;
            case 'chua-focus-o-nao':
                this.hienThongBao(
                    'Bấm vào ô nội dung trong bài giảng trước, rồi chèn lại.',
                    'loi'
                );
                break;
            case 'khong-thay-editor':
                this.hienThongBao(
                    'Chưa mở trình soạn bài giảng. Dùng nút Sao chép rồi dán thủ công.',
                    'loi'
                );
                break;
            default:
                this.hienThongBao(
                    'Không chèn được. Dùng nút Sao chép rồi dán thủ công.',
                    'loi'
                );
        }
    };

    private saoChep = async () => {
        if (!this.coNoiDung) {
            return;
        }
        let ok = false;
        try {
            await navigator.clipboard.writeText(this.ketQua);
            ok = true;
        } catch {
            ok = this.saoChepDuPhong(this.ketQua);
        }
        this.hienThongBao(
            ok
                ? 'Đã sao chép'
                : 'Không sao chép tự động được. Bôi đen chuỗi rồi nhấn Ctrl+C.',
            ok ? 'ok' : 'loi'
        );
    };

    private saoChepDuPhong(text: string): boolean {
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            return ok;
        } catch {
            return false;
        }
    }

    private hienThongBao(noiDung: string, loai: 'ok' | 'loi'): void {
        this.setState({ thongBao: noiDung, loaiThongBao: loai });
        if (this.timerThongBao) {
            clearTimeout(this.timerThongBao);
        }
        this.timerThongBao = setTimeout(
            () => this.setState({ thongBao: '', loaiThongBao: '' }),
            loai === 'ok' ? 2000 : 5000
        );
    }

    /**
     * Vị trí panel. Có tin từ Angular thì neo theo vùng nhìn thấy,
     * không thì rơi về position: fixed.
     */
    private viTriPanel(cao: number): React.CSSProperties {
        const { dayVungNhin } = this.state;
        if (dayVungNhin === null) {
            return { position: 'fixed', bottom: LE_DAY, right: LE_DAY };
        }
        return {
            position: 'absolute',
            top: Math.max(LE_DAY, dayVungNhin - cao - LE_DAY),
            right: LE_DAY
        };
    }

    public render(): React.ReactNode {
        const { moRong, kieuBlock, thongBao, loaiThongBao } = this.state;

        if (!moRong) {
            return (
                <div className="ctt-noi" style={this.viTriPanel(44)}>
                    <Button
                        variant="success"
                        className="ctt-noi__btn"
                        onClick={() => this.setState({ moRong: true })}
                    >
                        <FontAwesomeIcon icon={faSquareRootVariable} className="me-2" />
                        Soạn công thức toán
                    </Button>
                </div>
            );
        }

        return (
            <div className="ctt" style={this.viTriPanel(CHIEU_CAO_PANEL)}>
                <div className="ctt__head">
                    <span className="ctt__title">
                        <FontAwesomeIcon icon={faSquareRootVariable} className="me-2" />
                        Soạn công thức toán
                    </span>
                    <button
                        type="button"
                        className="ctt__dong"
                        aria-label="Thu gọn"
                        onClick={this.thuGon}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="ctt__kieu" role="group" aria-label="Kiểu hiển thị">
                    <button
                        type="button"
                        className={`ctt__kieu-btn${!kieuBlock ? ' is-active' : ''}`}
                        onClick={() => this.doiKieu(false)}
                        aria-pressed={!kieuBlock}
                    >
                        Trong dòng
                    </button>
                    <button
                        type="button"
                        className={`ctt__kieu-btn${kieuBlock ? ' is-active' : ''}`}
                        onClick={() => this.doiKieu(true)}
                        aria-pressed={kieuBlock}
                    >
                        Riêng dòng
                    </button>
                </div>

                {/* MathfieldElement được gắn vào đây bằng JS */}
                <div className="ctt__hop" ref={this.hopChuaRef} />

                <div className="ctt__mau-list">
                    {MAU_CONG_THUC.map((m) => (
                        <button
                            key={m.nhan}
                            type="button"
                            className="ctt__chip"
                            title={m.latex}
                            onClick={() => this.chenMau(m.latex)}
                        >
                            {m.nhan}
                        </button>
                    ))}
                </div>

                <code className={`ctt__ketqua${!this.coNoiDung ? ' is-empty' : ''}`}>
                    {this.coNoiDung ? this.ketQua : 'Chưa có công thức'}
                </code>

                <div className="ctt__actions">
                    <Button
                        variant="light"
                        size="sm"
                        disabled={!this.coNoiDung}
                        onClick={this.xoaHet}
                        title="Xóa"
                    >
                        <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        disabled={!this.coNoiDung}
                        onClick={this.saoChep}
                        title="Sao chép"
                    >
                        <FontAwesomeIcon icon={faCopy} />
                    </Button>
                    <Button
                        variant="success"
                        size="sm"
                        className="flex-grow-1"
                        disabled={!this.coNoiDung}
                        onClick={this.chenVaoBaiGiang}
                    >
                        Chèn vào bài giảng
                    </Button>
                </div>

                {thongBao ? (
                    <p
                        className={`ctt__thongbao${
                            loaiThongBao === 'ok' ? ' is-ok' : ' is-loi'
                        }`}
                        role="status"
                    >
                        {thongBao}
                    </p>
                ) : null}
            </div>
        );
    }
}
